<template>
  <Modal :show="show" :size="modalSize" @close="handleClose" class="modal">
    <template #header>
      <div class="header">
        <div class="logo">
          <Icon icon="mdi:linkedin" class="logo-icon" />
        </div>
        <h2 class="title">Import from LinkedIn Sales Navigator</h2>
      </div>
    </template>

    <Stepper :steps="stepLabels" :current-step="currentStepIndex" class="modal-stepper" />

    <div v-if="currentStep === 'url'" class="body">
      <div class="input-section">
        <label class="label"
          >Paste your LinkedIn Sales Navigator search URL to import and enrich contacts.</label
        >
        <div class="input-wrapper">
          <Input
            v-model="salesNavigatorUrl"
            placeholder="https://www.linkedin.com/sales/search/people?..."
            size="md"
            :error="urlError"
            @input="validateUrl"
          />
          <div v-if="isUrlValid && salesNavigatorUrl" class="valid">
            <Icon icon="mdi:check-circle" class="valid-icon" />
          </div>
        </div>
        <p class="hint">Copy the URL from your LinkedIn Sales Navigator search results page</p>
      </div>
    </div>

    <div v-else-if="currentStep === 'selection'" class="selection-content">
      <ContactSelectionStep
        :contacts="pageContacts"
        :selected-ids="selectedContactIds"
        :total-count="totalCount"
        :total-items="totalItems"
        :current-page="currentPageIndex"
        :per-page="perPage"
        :can-go-next="canGoNext"
        :can-go-prev="currentPageIndex > 0"
        :loading="isPaging"
        :cap-reached="capReached"
        @update:selected-ids="selectedContactIds = $event"
        @update:per-page="handlePerPageChange"
        @next-page="loadNextPage"
        @prev-page="loadPrevPage"
      />
    </div>

    <div v-else-if="currentStep === 'form'" class="form-content">
      <ContactsFoundCard :contacts-count="contactsCount" />

      <div class="options">
        <div class="option" @click.stop="formData.enrichEmail = !formData.enrichEmail">
          <Checkbox v-model="formData.enrichEmail" size="md" @click.stop />
          <span class="option-label">Enrich Email</span>
        </div>
        <div class="option" @click.stop="formData.enrichPhoneNumber = !formData.enrichPhoneNumber">
          <Checkbox v-model="formData.enrichPhoneNumber" size="md" @click.stop />
          <span class="option-label">Enrich Phone Number</span>
        </div>
      </div>

      <div class="fields">
        <div class="fields-row">
          <Field
            id="taskType"
            v-model="formData.taskType"
            :rules="{ required: ['Task Type'] }"
            name="taskType"
            v-slot="{ field, errorMessage }"
          >
            <Select
              :model-value="field.value"
              @update:model-value="field.onChange($event)"
              @blur="field.onBlur"
              label="Task Type"
              :options="taskTypeOptions"
              placeholder="Select task type"
              required
              :error="errorMessage"
              size="md"
            />
          </Field>

          <Field
            id="bucket"
            v-model="formData.bucketOptionId"
            :rules="{ required: ['Bucket'] }"
            name="bucket"
            v-slot="{ field, errorMessage }"
          >
            <Select
              :model-value="field.value"
              @update:model-value="field.onChange($event)"
              @blur="field.onBlur"
              label="Bucket"
              :options="bucketSelectOptions"
              placeholder="Select bucket"
              required
              :error="errorMessage"
              size="md"
            />
          </Field>
        </div>

        <Field
          id="followUpDate"
          v-model="formData.followUpDate"
          :rules="{ required: ['Follow up date'] }"
          name="followUpDate"
          v-slot="{ field, errorMessage }"
        >
          <div class="date-wrapper">
            <DateSelector
              :model-value="field.value"
              @update:model-value="field.onChange($event)"
              @blur="field.onBlur"
              label="Follow up date"
              placeholder="Select Date"
              :disable-past="false"
              required
            />
            <span v-if="errorMessage" class="error">{{ errorMessage }}</span>
          </div>
        </Field>
      </div>
    </div>

    <template #footer>
      <div v-if="currentStep === 'url'" class="footer">
        <Button
          variant="primary"
          size="lg"
          icon="mdi:magnify"
          class="search-btn"
          :disabled="!isUrlValid || isSearching"
          :loading="isSearching"
          @click="handleSearch"
          block
        >
          Search Contacts
        </Button>
      </div>
      <div v-else-if="currentStep === 'selection'" class="footer">
        <Button variant="secondary" size="md" @click="handleBackToUrl">Back</Button>
        <Button
          variant="primary"
          size="md"
          :disabled="selectedContactIds.length === 0"
          @click="handleContinueToForm"
        >
          Continue with {{ selectedContactIds.length }} Contact{{
            selectedContactIds.length !== 1 ? "s" : ""
          }}
        </Button>
      </div>
      <div v-else-if="currentStep === 'form'" class="footer">
        <Button variant="secondary" size="md" @click="handleBackToSelection">Back</Button>
        <Button
          v-tooltip="{
            content: USAGE_BLOCKED_TOOLTIP,
            disabled: !isUsageBlocked,
          }"
          variant="primary"
          size="md"
          icon="mdi:linkedin"
          :disabled="isUsageBlocked"
          @click="handleEnrichAndImport"
        >
          Enrich & Import Contacts
        </Button>
      </div>
    </template>
  </Modal>

  <LinkedInWarningModal
    :show="showWarning"
    :estimated-contacts="estimatedContacts"
    :loading="isSearching"
    @close="showWarning = false"
    @confirm="proceedWithSearch"
  />

  <LinkedInConfirmationModal
    :show="showConfirmation"
    :contacts-count="contactsCount"
    :loading="isSubmitting"
    @close="showConfirmation = false"
    @confirm="confirmEnrichAndImport"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import {
  Modal,
  Button,
  Input,
  Icon,
  Select,
  Checkbox,
  DateSelector,
  Stepper,
} from "@/components/ui";
import { Field, defineRule, useForm } from "vee-validate";
import { TaskType } from "@/utils/enums";
import { getTaskTypeIcon } from "@/utils/iconMapping";
import { useBucketOptions } from "@/composables/bucket/useBucketOptions";
import {
  browseLinkedInSearchPage,
  startLinkedInImportJob,
} from "@/composables/contacts/useLinkedInSearch";
import { useAppStore } from "@/composables/app/useAppStore";
import { useBillingStatus, USAGE_BLOCKED_TOOLTIP } from "@/composables/billing/useBillingStatus";
import { useToast } from "@/composables/ui/useToast";
import ContactsFoundCard from "./ContactsFoundCard.vue";
import ContactSelectionStep from "./ContactSelectionStep.vue";
import LinkedInWarningModal from "./LinkedInWarningModal.vue";
import LinkedInConfirmationModal from "./LinkedInConfirmationModal.vue";
import { useQueryClient } from "@tanstack/vue-query";
import type { LinkedInSearchProspect } from "@/dtos/Unipile.dto";

defineRule("required", (value: unknown, [fieldName = "Field"]: [string?]) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return `${fieldName} is required`;
  }
  return true;
});

const { validate } = useForm();

const PAGE_SIZE = 50;

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

const queryClient = useQueryClient();

const { account } = useAppStore();
const organizationId = computed(() => account.value?.organizationId);

const { showUsageBasedFeaturesModalOnce, isUsageBlocked } = useBillingStatus();

type Step = "url" | "selection" | "form";
const currentStep = ref<Step>("url");
const stepLabels = ["Enter URL", "Choose Contacts", "Set Options"];
const currentStepIndex = computed(() => {
  const stepMap: Record<Step, number> = { url: 0, selection: 1, form: 2 };
  return stepMap[currentStep.value];
});

const modalSize = computed(() => {
  if (currentStep.value === "selection") return "xl";
  return "md";
});

const salesNavigatorUrl = ref("");
const urlError = ref("");
const isUrlValid = ref(false);
const isSearching = ref(false);
const isPaging = ref(false);
const contactsCount = ref<number>(0);
const showConfirmation = ref(false);
const showWarning = ref(false);
const estimatedContacts = ref<number>(0);
const isSubmitting = ref(false);

const sessionId = ref<string | null>(null);
const pageContacts = ref<LinkedInSearchProspect[]>([]);
const totalCount = ref<number | null>(null);
const totalItems = ref<number>(0);
const currentPageIndex = ref<number>(0);
const perPage = ref<number>(PAGE_SIZE);
const hasMore = ref<boolean>(false);
const capReached = ref<boolean>(false);

const canGoNext = computed(() => {
  if (hasMore.value) return true;
  const next = (currentPageIndex.value + 1) * perPage.value;
  return next < totalItems.value;
});

const selectedContactIds = ref<string[]>([]);

const $toast = useToast();

const formData = ref({
  enrichEmail: true,
  enrichPhoneNumber: true,
  taskType: null as string | null,
  bucketOptionId: null as string | null,
  followUpDate: null as string | null,
});

const bucketOptionsParams = computed(() => {
  if (!organizationId.value) return null;
  return { organizationId: organizationId.value };
});

const { data: bucketOptionsData } = useBucketOptions(bucketOptionsParams);

const bucketSelectOptions = computed(() => {
  const options = bucketOptionsData.value || [];
  return options.map((bucket: any) => ({
    value: bucket.id,
    label: bucket.name,
    icon: "fa6-solid:bucket",
    iconColor: bucket.colorHex,
  }));
});

const taskTypeOptions = computed(() => {
  return Object.values(TaskType).map((task) => ({
    value: task,
    label: task.charAt(0).toUpperCase() + task.slice(1),
    icon: getTaskTypeIcon(task),
  }));
});

const validateUrl = () => {
  const url = salesNavigatorUrl.value.trim();
  urlError.value = "";

  if (!url) {
    isUrlValid.value = false;
    return;
  }

  const salesNavigatorPattern = /^https?:\/\/(www\.)?linkedin\.com\/sales\/search\/people/;

  if (!salesNavigatorPattern.test(url)) {
    urlError.value = "Please enter a valid LinkedIn Sales Navigator URL";
    isUrlValid.value = false;
    return;
  }

  isUrlValid.value = true;
  urlError.value = "";
};

const handleSearch = async () => {
  if (!isUrlValid.value) {
    validateUrl();
    return;
  }

  isSearching.value = true;
  try {
    const data = await browseLinkedInSearchPage({
      url: salesNavigatorUrl.value.trim(),
      limit: perPage.value,
      page: 0,
    });
    sessionId.value = data.sessionId;
    pageContacts.value = data.items;
    totalCount.value = data.totalCount;
    totalItems.value = data.totalItems;
    currentPageIndex.value = data.page;
    perPage.value = data.perPage;
    hasMore.value = data.hasMore;
    capReached.value = data.capReached;

    selectedContactIds.value = [];

    if ((totalCount.value ?? 0) > 100) {
      estimatedContacts.value = totalCount.value ?? data.items.length;
      showWarning.value = true;
      isSearching.value = false;
      return;
    }

    currentStep.value = "selection";
  } catch (error: any) {
    console.error("Error searching contacts:", error);
    urlError.value =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to search contacts. Please try again.";
    isUrlValid.value = false;
  } finally {
    isSearching.value = false;
  }
};

const proceedWithSearch = () => {
  showWarning.value = false;
  if (sessionId.value) {
    currentStep.value = "selection";
  }
};

const fetchPage = async (targetPage: number, targetPerPage: number) => {
  if (!sessionId.value || isPaging.value) return;
  isPaging.value = true;
  try {
    const data = await browseLinkedInSearchPage({
      url: salesNavigatorUrl.value.trim(),
      sessionId: sessionId.value,
      page: Math.max(0, targetPage),
      limit: targetPerPage,
    });
    pageContacts.value = data.items;
    totalCount.value = data.totalCount ?? totalCount.value;
    totalItems.value = data.totalItems;
    currentPageIndex.value = data.page;
    perPage.value = data.perPage;
    hasMore.value = data.hasMore;
    capReached.value = data.capReached;
  } catch (error: any) {
    $toast?.error(error?.message || "Failed to load page.");
  } finally {
    isPaging.value = false;
  }
};

const loadNextPage = () => fetchPage(currentPageIndex.value + 1, perPage.value);
const loadPrevPage = () => fetchPage(currentPageIndex.value - 1, perPage.value);

const handlePerPageChange = (newPerPage: number) => {
  if (newPerPage === perPage.value) return;
  return fetchPage(0, newPerPage);
};

const handleBackToUrl = () => {
  currentStep.value = "url";
  resetSelectionState();
};

const handleContinueToForm = () => {
  if (selectedContactIds.value.length === 0) return;
  contactsCount.value = selectedContactIds.value.length;
  currentStep.value = "form";
};

const handleBackToSelection = () => {
  currentStep.value = "selection";
  formData.value = {
    enrichEmail: true,
    enrichPhoneNumber: true,
    taskType: null,
    bucketOptionId: null,
    followUpDate: null,
  };
};

const handleEnrichAndImport = async () => {
  const result = await validate();
  if (!result.valid) return;

  showConfirmation.value = true;
};

const confirmEnrichAndImport = async () => {
  if (!sessionId.value) {
    $toast?.error("Search session expired. Please re-run the search.");
    return;
  }

  await showUsageBasedFeaturesModalOnce("linkedin_import", async () => {
    isSubmitting.value = true;

    try {
      const result = await startLinkedInImportJob({
        sessionId: sessionId.value!,
        selectedProspectIds: selectedContactIds.value,
        taskType: formData.value.taskType ?? undefined,
        bucketOptionId: formData.value.bucketOptionId ?? undefined,
        followUpDate: formData.value.followUpDate ?? undefined,
        enrichEmail: formData.value.enrichEmail,
        enrichPhoneNumber: formData.value.enrichPhoneNumber,
      });

      $toast?.info(
        `Importing ${result.total} contact${result.total !== 1 ? "s" : ""}. We'll notify you when it's complete.`
      );

      queryClient.invalidateQueries({ queryKey: ["notifications"] });

      showConfirmation.value = false;
      handleClose();
    } catch (error: any) {
      console.error("Error enriching contacts:", error);
      $toast?.error(error?.message || "Failed to start import. Please try again.");
    } finally {
      isSubmitting.value = false;
    }
  });
};

const resetSelectionState = () => {
  sessionId.value = null;
  pageContacts.value = [];
  totalCount.value = null;
  totalItems.value = 0;
  currentPageIndex.value = 0;
  perPage.value = PAGE_SIZE;
  hasMore.value = false;
  capReached.value = false;
  selectedContactIds.value = [];
};

const handleClose = () => {
  salesNavigatorUrl.value = "";
  urlError.value = "";
  isUrlValid.value = false;
  currentStep.value = "url";
  contactsCount.value = 0;
  showConfirmation.value = false;
  showWarning.value = false;
  estimatedContacts.value = 0;
  resetSelectionState();
  formData.value = {
    enrichEmail: true,
    enrichPhoneNumber: true,
    taskType: null,
    bucketOptionId: null,
    followUpDate: null,
  };
  emit("close");
};

watch(
  () => props.show,
  (newVal) => {
    if (!newVal) {
      handleClose();
    }
  }
);
</script>

<style scoped>
.header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: #0077b5;
  border-radius: 6px;
  flex-shrink: 0;
}

.logo-icon {
  color: white;
  font-size: 20px;
}

.title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
}

.input-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  font-size: 0.875rem;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.5;
}

.input-wrapper {
  position: relative;
  width: 100%;
}

.valid {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  pointer-events: none;
}

.valid-icon {
  color: #10b981;
  font-size: 20px;
}

.hint {
  margin: 0;
  font-size: 0.75rem;
  color: #9ca3af;
  line-height: 1.4;
}

.footer {
  display: flex;
  gap: 12px;
  border-top: none;
  justify-content: flex-end;
  width: 100%;
}

.footer :deep(.ui-button--disabled) {
  pointer-events: auto;
}

.footer:has(.search-btn) {
  justify-content: center;
}

.search-btn {
  width: 100%;
}

.search-btn :deep(.ui-button) {
  width: 100%;
  font-size: 0.875rem;
  font-weight: 500;
}

.modal :deep(.ui-modal__body) {
  padding-bottom: 0;
}

.modal :deep(.ui-modal__footer) {
  padding: 12px 24px;
}

.modal-stepper {
  margin: 8px 0 8px;
}

.modal-stepper :deep(.stepper) {
  margin-bottom: 0;
  padding: 0 8px;
}

.selection-content {
  min-height: 300px;
  padding: 8px 24px 0;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
}

.options {
  display: flex;
  gap: 24px;
  padding: 0;
  margin: 0;
}

.option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.option-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.fields-row {
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 100%;
}

.fields-row > * {
  flex: 1;
  min-width: 0;
}

.date-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.error {
  font-size: 0.75rem;
  color: #dc3545;
  margin-top: 4px;
}
</style>
